// Valid JSON Schema types (draft-07)
export const JSON_SCHEMA_TYPES = ['string', 'number', 'integer', 'boolean', 'object', 'array', 'null'] as const;
export type JsonSchemaType = (typeof JSON_SCHEMA_TYPES)[number];

// Composite modes
export const COMPOSITE_MODES = ['anyOf', 'oneOf', 'allOf'] as const;
export type CompositeMode = (typeof COMPOSITE_MODES)[number];

// Internal types for visual editor (JSON Schema types minus 'null', plus 'enum', 'composite', and 'unknown' as abstractions)
export type SchemaPropertyType = Exclude<JsonSchemaType, 'null'> | 'enum' | 'composite' | 'unknown';

export type SchemaProperty = {
  id: string;
  name: string;
  type: SchemaPropertyType;
  description?: string;
  children?: SchemaProperty[];
  arrayItemType?: SchemaPropertyType;
  arrayItemChildren?: SchemaProperty[];
  enumValues?: string[];
  // For composite types (anyOf/oneOf/allOf)
  compositeMode?: CompositeMode;
  schemas?: SchemaProperty[];
};

export const generateId = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;

export type SchemaModel = {
  properties: SchemaProperty[];
};

/**
 * Find duplicate property names in a list (single level)
 * Returns set of names that appear more than once
 */
export const findDuplicateNames = (properties: SchemaProperty[]): Set<string> => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  for (const prop of properties) {
    if (seen.has(prop.name)) {
      duplicates.add(prop.name);
    }
    seen.add(prop.name);
  }

  return duplicates;
};

/**
 * Check if model has any duplicate names at any nesting level
 * Returns true if duplicates exist anywhere
 */
export const hasDuplicateNamesDeep = (model: SchemaModel): boolean => {
  const checkProperties = (properties: SchemaProperty[]): boolean => {
    if (findDuplicateNames(properties).size > 0) {
      return true;
    }

    for (const prop of properties) {
      if (prop.children && checkProperties(prop.children)) {
        return true;
      }
      if (prop.arrayItemChildren && checkProperties(prop.arrayItemChildren)) {
        return true;
      }
    }

    return false;
  };

  return checkProperties(model.properties);
};

export type ParseResult =
  | { success: true; model: SchemaModel }
  | { success: false; error: 'invalidJson' | 'invalidSchema' | 'duplicateKeys' };

type JsonSchemaNode = Record<string, unknown>;

const isObject = (value: unknown): value is JsonSchemaNode => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

/**
 * Detect duplicate keys in raw JSON string
 * This may seem like a complex function, however it is necessary to check for duplicates in the JSON string
 * Because JSON editor value is string, and we don't want to allow duplicates in the JSON string
 * JSON.parse deduplicates the keys, but we need to check for duplicates in the original string
 * Returns true if duplicates found
 */
export const hasDuplicateKeys = (jsonString: string): boolean => {
  const contextStack: (Set<string> | null)[] = [];
  let inString = false;
  let escaped = false;
  let currentKey = '';
  let collectingKey = false;
  let afterColon = false;

  const isInObject = () => {
    const current = contextStack[contextStack.length - 1];
    return current !== null && current !== undefined;
  };

  for (let i = 0; i < jsonString.length; i++) {
    const char = jsonString[i];

    if (escaped) {
      if (collectingKey) currentKey += char;
      escaped = false;
      continue;
    }

    if (char === '\\') {
      escaped = true;
      if (collectingKey) currentKey += char;
      continue;
    }

    if (char === '"') {
      if (!inString) {
        inString = true;
        if (!afterColon && isInObject()) {
          collectingKey = true;
          currentKey = '';
        }
      } else {
        inString = false;
        if (collectingKey) {
          collectingKey = false;
          const currentLevel = contextStack[contextStack.length - 1];
          if (currentLevel?.has(currentKey)) {
            return true;
          }
          currentLevel?.add(currentKey);
        }
      }
      continue;
    }

    if (inString) {
      if (collectingKey) currentKey += char;
      continue;
    }

    if (char === '{') {
      contextStack.push(new Set());
      afterColon = false;
    } else if (char === '}') {
      contextStack.pop();
      afterColon = false;
    } else if (char === '[') {
      contextStack.push(null);
      afterColon = false;
    } else if (char === ']') {
      contextStack.pop();
      afterColon = false;
    } else if (char === ':') {
      afterColon = true;
    } else if (char === ',') {
      afterColon = false;
    }
  }

  return false;
};

/**
 * Parse JSON Schema string into internal model
 * Resolves $refs and flattens the structure
 * Returns error if duplicate keys detected
 */
export const parseSchema = (jsonString: string): ParseResult => {
  // Treat empty/whitespace as a valid empty schema so the visual editor is usable from scratch
  if (!jsonString || jsonString.trim() === '') {
    return { success: true, model: { properties: [] } };
  }

  if (hasDuplicateKeys(jsonString)) {
    return { success: false, error: 'duplicateKeys' };
  }

  try {
    const schema = JSON.parse(jsonString);

    // Treat bare {} as a valid empty schema
    if (isObject(schema) && Object.keys(schema).length === 0) {
      return { success: true, model: { properties: [] } };
    }

    if (!isObject(schema) || schema.type !== 'object') {
      return { success: false, error: 'invalidSchema' };
    }

    const defs: Record<string, JsonSchemaNode> = {
      ...(isObject(schema.$defs) ? (schema.$defs as Record<string, JsonSchemaNode>) : {}),
      ...(isObject(schema.definitions) ? (schema.definitions as Record<string, JsonSchemaNode>) : {}),
    };

    const properties = parseProperties(schema.properties, defs);
    return { success: true, model: { properties } };
  } catch {
    return { success: false, error: 'invalidJson' };
  }
};

const parseProperties = (properties: unknown, defs: Record<string, JsonSchemaNode>): SchemaProperty[] => {
  if (!isObject(properties)) {
    return [];
  }

  return Object.entries(properties).map(([name, prop]) => parseProperty(name, prop, defs));
};

const parseProperty = (name: string, prop: unknown, defs: Record<string, JsonSchemaNode>): SchemaProperty => {
  if (!isObject(prop)) {
    return { id: generateId(), name, type: 'string' };
  }

  if ('$ref' in prop && typeof prop.$ref === 'string') {
    const refMatch = prop.$ref.match(/^#\/(\$defs|definitions)\/(.+)$/);
    if (refMatch && defs[refMatch[2]]) {
      return parseProperty(name, defs[refMatch[2]], defs);
    }
  }

  const description = typeof prop.description === 'string' ? prop.description : undefined;

  // Handle composite types (anyOf/oneOf/allOf)
  const compositeModes: CompositeMode[] = ['anyOf', 'oneOf', 'allOf'];
  for (const mode of compositeModes) {
    if (mode in prop && Array.isArray(prop[mode])) {
      const schemas = (prop[mode] as unknown[])
        .filter(isObject)
        .map((schema, index) => parseProperty(`schema_${index + 1}`, schema, defs));
      return {
        id: generateId(),
        name,
        type: 'composite',
        description,
        compositeMode: mode,
        schemas,
      };
    }
  }

  // Handle enum
  if ('enum' in prop && Array.isArray(prop.enum)) {
    return {
      id: generateId(),
      name,
      type: 'enum',
      description,
      enumValues: prop.enum.filter((v): v is string => typeof v === 'string'),
    };
  }

  const type = prop.type as string;

  // Handle object
  if (type === 'object') {
    return {
      id: generateId(),
      name,
      type: 'object',
      description,
      children: parseProperties(prop.properties, defs),
    };
  }

  // Handle array
  if (type === 'array' && isObject(prop.items)) {
    const items = prop.items;

    // Resolve $ref in items
    if ('$ref' in items && typeof items.$ref === 'string') {
      const refMatch = items.$ref.match(/^#\/(\$defs|definitions)\/(.+)$/);
      if (refMatch && defs[refMatch[2]]) {
        const resolved = parseProperty('item', defs[refMatch[2]], defs);
        return {
          id: generateId(),
          name,
          type: 'array',
          description,
          arrayItemType: resolved.type,
          arrayItemChildren: resolved.children,
        };
      }
    }

    // Handle enum in items
    if ('enum' in items && Array.isArray(items.enum)) {
      return {
        id: generateId(),
        name,
        type: 'array',
        description,
        arrayItemType: 'enum',
        enumValues: items.enum.filter((v): v is string => typeof v === 'string'),
      };
    }

    const itemType = items.type as SchemaPropertyType;

    if (itemType === 'object') {
      return {
        id: generateId(),
        name,
        type: 'array',
        description,
        arrayItemType: 'object',
        arrayItemChildren: parseProperties(items.properties, defs),
      };
    }

    return {
      id: generateId(),
      name,
      type: 'array',
      description,
      arrayItemType: itemType || 'string',
    };
  }

  // Handle primitive types
  const validTypes: SchemaPropertyType[] = ['string', 'number', 'boolean', 'integer'];
  const resolvedType: SchemaPropertyType = validTypes.includes(type as SchemaPropertyType)
    ? (type as SchemaPropertyType)
    : 'unknown';

  return {
    id: generateId(),
    name,
    type: resolvedType,
    description,
  };
};

/**
 * Serialize internal model back to JSON Schema string
 * Produces clean, ref-free schema
 */
export const serializeSchema = (model: SchemaModel): string => {
  const schema: JsonSchemaNode = {
    type: 'object',
    properties: serializeProperties(model.properties),
    required: model.properties.map(p => p.name),
    additionalProperties: false,
  };

  return JSON.stringify(schema, null, 2);
};

const serializeProperties = (properties: SchemaProperty[]): JsonSchemaNode => {
  const result: JsonSchemaNode = {};

  for (const prop of properties) {
    result[prop.name] = serializeProperty(prop);
  }

  return result;
};

const serializeProperty = (prop: SchemaProperty): JsonSchemaNode => {
  const base: JsonSchemaNode = {};

  if (prop.description) {
    base.description = prop.description;
  }

  switch (prop.type) {
    case 'composite': {
      const mode = prop.compositeMode || 'oneOf';
      const schemas = (prop.schemas || []).map(schema => serializeCompositeSchema(schema));
      return {
        ...base,
        [mode]: schemas,
      };
    }

    case 'enum':
      return {
        ...base,
        type: 'string',
        enum: prop.enumValues || [],
      };

    case 'object':
      return {
        ...base,
        type: 'object',
        properties: serializeProperties(prop.children || []),
        required: (prop.children || []).map(c => c.name),
        additionalProperties: false,
      };

    case 'array': {
      const items = serializeArrayItems(prop);
      return {
        ...base,
        type: 'array',
        items,
      };
    }

    default:
      return {
        ...base,
        type: prop.type,
      };
  }
};

/**
 * Serialize a schema within a composite (anyOf/oneOf/allOf)
 * These schemas don't have a name, they're just type definitions
 */
const serializeCompositeSchema = (prop: SchemaProperty): JsonSchemaNode => {
  const base: JsonSchemaNode = {};

  if (prop.description) {
    base.description = prop.description;
  }

  switch (prop.type) {
    case 'composite': {
      const mode = prop.compositeMode || 'oneOf';
      const schemas = (prop.schemas || []).map(schema => serializeCompositeSchema(schema));
      return {
        ...base,
        [mode]: schemas,
      };
    }

    case 'enum':
      return {
        ...base,
        type: 'string',
        enum: prop.enumValues || [],
      };

    case 'object':
      return {
        ...base,
        type: 'object',
        properties: serializeProperties(prop.children || []),
        required: (prop.children || []).map(c => c.name),
        additionalProperties: false,
      };

    case 'array': {
      const items = serializeArrayItems(prop);
      return {
        ...base,
        type: 'array',
        items,
      };
    }

    default:
      return {
        ...base,
        type: prop.type,
      };
  }
};

const serializeArrayItems = (prop: SchemaProperty): JsonSchemaNode => {
  if (prop.arrayItemType === 'enum') {
    return {
      type: 'string',
      enum: prop.enumValues || [],
    };
  }

  if (prop.arrayItemType === 'object') {
    return {
      type: 'object',
      properties: serializeProperties(prop.arrayItemChildren || []),
      required: (prop.arrayItemChildren || []).map(c => c.name),
      additionalProperties: false,
    };
  }

  return {
    type: prop.arrayItemType || 'string',
  };
};
